//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import $ from "jquery";

import { IContentImportFromUrlRequest } from '@apis/content-library/contentLibraryApiModel';
import {
  ISearchLabelsAutocompleteResponse,
  ISearchLabelsResponse,
  ManageLabelTaskStatus,
  SortLabelsRequest,
} from '@apis/gatewayMediaApiModel';
import Cuid from '@models/Cuid';
import { IDeetsItemsResponse } from '@models/IDeetsModel';
import { IRightsRequestState } from '@models/IItemMetadata';
import { ILabel } from '@models/ILabel';
import PartialCuid from '@models/PartialCuid';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

interface IPostToLibraryByUrlResponse {
  cuids: Cuid[];
}

function gatewayMediaFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/media/${endpoint}`,
    init,
  );
}

/**
 * Ingests an Instagram/TikTok URL and adds it to the Media Library with the corresponding label
 * @param url: permalink to an Instagram or TikTok post
 * @returns List containing the Cuid that corresponds to the ingested item
 */
export async function addPostToLibraryByUrl({
  collectionIds,
  labels,
  metadata,
  url,
}: IContentImportFromUrlRequest): Promise<IPostToLibraryByUrlResponse> {
  const res = await gatewayMediaFetch('library/add-by-url', {
    method: 'POST',
    body: JSON.stringify({
      collectionIds,
      labels,
      metadata,
      url,
    }),
  });

  return await res.json();
}

/**
 * Update the preferred caption for the provided cuid.
 * /api/v1/media/library/media-items/:cuid/preferred-caption
 *
 * @param cuid
 * @param preferredCaption
 */
export const updatePreferredCaption = async (
  cuid: Cuid,
  preferredCaption: string,
  init?: RequestInit,
): Promise<void> => {
  await gatewayMediaFetch(`library/media-items/${cuid}/preferred-caption`, {
    ...(init ?? {}),
    method: 'PUT',
    body: JSON.stringify({
      preferredCaption,
    }),
  });
};

/**
 * Remove the preferred caption for the provided cuid.
 * /api/v1/media/library/media-items/:cuid/preferred-caption
 *
 * @param cuid
 */
export const removePreferredCaption = async (
  cuid: Cuid,
  init?: RequestInit,
): Promise<void> => {
  await gatewayMediaFetch(`library/media-items/${cuid}/preferred-caption`, {
    ...(init ?? {}),
    method: 'DELETE',
  });
};

export interface IRightsRequestStatusResponse {
  data: Record<PartialCuid, IRightsRequestState>;
}

/**
 * Gets the current rights request status for a list of cuids
 * /api/v1/media/rights-request-status
 *
 * @param cuids
 * @returns An object whose keys are partialcuids and whose values are Rights Request States
 */
export async function getRightsRequestStatuses(
  cuids: PartialCuid[],
  init?: RequestInit,
): Promise<IRightsRequestStatusResponse> {
  const res = await gatewayMediaFetch('rights-request-status', {
    ...(init ?? {}),
    method: 'POST',
    body: JSON.stringify({
      cuids,
    }),
  });

  return await res.json();
}

/**
 * Gets label suggestions from the current client's set of labels, based
 * on the given search term
 *
 * /api/v1/media/library/labels/search
 */
export const searchLabelsDeprecated = async (
  searchTerm: string,
): Promise<ILabel[]> => {
  const params = $.param({ query: searchTerm });
  const res = await gatewayMediaFetch(`library/labels/search?${params}`);
  const { labels } = await res.json<{ labels: Record<string, number> }>();

  return Object.entries(labels).map(([label, count]) => {
    return {
      label,
      count,
      value: label,
    };
  });
};

/**
 * Gets label suggestions from the current client's set of labels, based
 * on the given search term
 *
 * /api/v1/media/library/labels-management/search
 */
export const searchLabels = async (
  sortFields: SortLabelsRequest[],
  query?: string,
  offset?: number,
  limit?: number,
  init?: RequestInit,
): Promise<ISearchLabelsResponse> => {
  const res = await gatewayMediaFetch(`library/labels-management/search`, {
    ...(init ? init : {}),
    method: 'POST',
    body: JSON.stringify({
      query,
      offset,
      limit,
      sortFields,
    }),
  });
  return res.json();
};

/**
 * Search media labels from the current client's set of labels, based
 * on the given search term
 *
 * /api/v1/media/library/labels/search?query=${query}
 */
export const searchLabelsAutocomplete = async (
  query: string,
  init?: RequestInit,
): Promise<ISearchLabelsAutocompleteResponse> => {
  const queryParams = makeParams({ query });
  const res = await gatewayMediaFetch(`library/labels/search?${queryParams}`, {
    ...(init ? init : {}),
    method: 'GET',
  });

  return res.json<ISearchLabelsAutocompleteResponse>();
};

/**
 * Updates the label to newLabel on all DTS items that label is present on for the current client
 *
 * /api/v1/media/library/labels-management/:userTag
 */
export const updateLabel = async (
  label: string,
  newUserTag: string,
): Promise<number> => {
  const res = await gatewayMediaFetch(`library/labels-management/${label}`, {
    method: 'PUT',
    body: JSON.stringify({
      newUserTag,
    }),
  });
  return res.status === 204 ? -1 : res.json();
};

/**
 * Deletes the label from all DTS items that the label is present on for the current client
 *
 * /api/v1/media/library/labels-management/:userTag
 */
export const deleteLabel = async (label: string): Promise<number> => {
  const res = await gatewayMediaFetch(`library/labels-management/${label}`, {
    method: 'DELETE',
  });
  return res.status === 204 ? -1 : res.json();
};

/**
 * Gets the status of the bulk update joule job for the passed instanceId
 *
 * /api/v1/media/library/labels-management/bulk-update-status/:instanceId
 */
export const pollBulkUpdateStatus = async (
  instanceId: number,
): Promise<ManageLabelTaskStatus> => {
  const res = await gatewayMediaFetch(
    `library/labels-management/bulk-update-status/${instanceId}`,
    {
      method: 'GET',
    },
  );
  return res.json();
};

/**
 * Gets the client's most commonly-used labels, optionally providing a number of labels to return.
 * Will retreive the most common 500 labels by default.
 *
 * /api/v1/media/labels/most-common
 */
export const getMostCommonLabels = async (
  limit?: number,
  offset?: number,
  init?: RequestInit,
): Promise<ILabel[]> => {
  const params = $.param({
    limit,
    offset,
  });
  const res = await gatewayMediaFetch(
    `library/labels/most-common?${params}`,
    init,
  );
  const { labels } = await res.json<{ labels: Record<string, number> }>();

  return Object.entries(labels).map(([label, count]) => {
    return {
      label,
      count,
      value: label,
    };
  });
};

/**
 * Gets the deets item associated with a given id
 *
 * /api/v1/media/deets-item
 */
export const getDeetsItemFromId = async (
  deetsId: string,
): Promise<IDeetsItemsResponse> => {
  const params = $.param({ deetsId });
  const res = await gatewayMediaFetch(`deets-item?${params}`);
  return await res.json();
};
